import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Store } from '@ngrx/store';
import { flatMap, map, take } from 'rxjs/operators';
import { get } from 'lodash';
import { of } from 'rxjs/observable/of';
import { Subject } from "rxjs";

import { ActionCreatorAbstract } from '@ispm/core/classes/action-creator.abstract';
import { ConditionalItem } from '@ispm/shared/classes/conditional-item.class';
import { ConfigurationStoreHandler } from '../../../configuration/services/configuration-store-handler.service';
import { DIAGNOSTIC_ORDER_ACTIONS } from '../constants/diagnostic-order.constants';
import { IDiagnosticOrder } from '../interfaces/diagnostic-order.interface';
import { IDiagnosticOrderState } from '../store/diagnostic-order.reducer';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';

@Injectable()
export class DiagnosticOrderActionsService extends ActionCreatorAbstract {
  private triggerLetterGenerate$: Subject<any> = new Subject();
  private triggerUpdateAttachments$: Subject<any> = new Subject();

  constructor(
    store$: Store<IgenomicsState>,
    private configurationStoreHandler: ConfigurationStoreHandler
  ) {
    super(store$, 'tests', 'diagnosticOrders');
  }

  initDiagnosticOrder() {
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.INIT_DIAGNOSTIC_ORDER_CONFIG
    });
  }

  initDiagnosticOrderState() {
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.INIT_DIAGNOSTIC_ORDER_STATE
    });
  }

  setDiagnosticOrder(diagnosticOrder: IDiagnosticOrder) {
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.SET_DIAGNOSTIC_ORDER_CONFIG,
      payload: diagnosticOrder
    });
  }

  updateDiagnosticOrderFields(object: Object) {
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.UPDATE_DIAGNOSTIC_ORDER_FIELDS,
      payload: object
    });
  }

  setAllDiagnosticOrders(diagnosticOrders: IDiagnosticOrder[]) {
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.SET_ALL_DIAGNOSTIC_ORDERS,
      payload: diagnosticOrders
    });
  }

  updateNotes(notes: number) {
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.SET_NOTES,
      payload: notes
    });
  }

  currentNotes(notesContent:string[]){
    this.store.dispatch({
      type: DIAGNOSTIC_ORDER_ACTIONS.SET_NOTES_CONTENT,
      payload: notesContent
    });
  }

  getState<T = IDiagnosticOrderState>(...props): Observable<T> {
    return this.store.pipe(
      map(state => props.length > 0 ? get(state, props.join('.')) : state)
    );
  }

  verifyProceedToNextStepAllowed(): Observable<{ valid: boolean, invalidField: string }> {
    return this.configurationStoreHandler.getConfigurationForCurrentStep()
      .pipe(
        take(1),
        flatMap((stepConfiguration) => {
          if (!stepConfiguration) {
            return of({ valid: true, invalidField: null});
          }

          return this.getState('order')
            .pipe(
              take(1),
              map(order => {
                let validationResources, valid = true, invalidField;

                requiredToProceedLoop:

                  for (const property in stepConfiguration.fieldsValidation.requiredToProceed) {
                    const resourceConfigurations = get(stepConfiguration.fieldsValidation.requiredToProceed, property, []);

                    for (const index in resourceConfigurations) {
                      const item = resourceConfigurations[index];
                      const requiredFields = get(item, 'fields', null);
                      const conditions = get(item, 'conditions', null);

                      if (!requiredFields) {
                        continue;
                      }

                      switch (property) {
                        case 'diagnosticOrder':
                          validationResources = order;
                          break;
                        default:
                          if (conditions) {
                            validationResources = get(order, `${property}`);
                          } else {
                            validationResources = get(order, `${property}[${index}]`);
                          }
                          break;
                      }

                      validationResources = Array.isArray(validationResources) ? validationResources : [validationResources];

                      for (const validationResource of validationResources) {
                        if (conditions) {
                          valid =  conditions.every(cond => ConditionalItem.from(validationResource, cond).verify());
                        }

                        valid = valid && requiredFields.every(field => {
                          let path = field, restrictNull = true, restrictBlank = false;

                          if (field && typeof field === 'object') {
                            path = field.path;
                            restrictBlank = get(field, 'restrictBlank', restrictBlank);
                            restrictNull = get(field, 'restrictNull', restrictNull);
                          }

                          if (restrictNull && get(validationResource, path, null) == null) {
                            invalidField = path;
                            return false;
                          }
                          else if (restrictBlank && get(validationResource, path, '').toString() === '') {
                            invalidField = path;
                            return false;
                          }
                          return true;
                        });

                        if (!valid) {
                          break requiredToProceedLoop;
                        }
                      }
                    }
                  }

                return { valid, invalidField};
              })
            );
        })
      );
  }

  getEditAllowedForDiscipline(rules?: { [discipline: string]: boolean}[]): Observable<boolean> {
    return !rules || !Object.keys(rules).length
      ? of(true)
      : this.getState('order').pipe(
        take(1),
        map(order => (<any> order).discipline),
        map(discipline => discipline && !!rules[`${discipline.code}`])
      );
  }

  getDiscipline(): Observable<any> {
    return this.getState('order').pipe(
      take(1),
      map(order => (<any> order).discipline)
    );
  }

  setTriggerLetterGenerate(data: any) {
    this.triggerLetterGenerate$.next(data);
  }

  getTriggerLetterGenerate() {
    return this.triggerLetterGenerate$.asObservable();
  }

  setTriggerUpdateAttachments(data: any) {
    this.triggerUpdateAttachments$.next(data);
  }

  getTriggerUpdateAttachments() {
    return this.triggerUpdateAttachments$.asObservable();
  }
}
